import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './TreatmentSidebar.scss';
import { LanguageContext } from '../../context/LanguageContext';
import useAnalytics from '../../hooks/useAnalytics';
import { getCategories } from '../../services/categoryService';

// Türkçe karakter düzeltme fonksiyonu
const fixTurkishChars = (str) => {
    if (!str) return '';
    
    const turkishChars = {
        'Ä°': 'İ',
        'Ä±': 'ı',
        'ÅŸ': 'Ş',
        'Åş': 'ş',
        'ÄŸ': 'ğ',
        'Ä': 'Ğ',
        'ü': 'ü',
        'Ü': 'Ü',
        'ö': 'ö',
        'Ö': 'Ö',
        'ç': 'ç',
        'Ç': 'Ç',
        'Diş': 'Diş',
        'Hekimliği': 'Hekimliği',
        'iği': 'iği',
        'liği': 'liği'
    };

    return str.replace(
        new RegExp(Object.keys(turkishChars).join('|'), 'g'),
        match => turkishChars[match]
    );
};

const TreatmentSidebar = ({ treatment }) => {
    const { t, currentLang } = useContext(LanguageContext);
    const { trackEvent } = useAnalytics();
    const [phone, setPhone] = useState('');
    const [callStatus, setCallStatus] = useState(null);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const loadCategories = async () => {
            if (!categories.length) {
                try {
                    setLoading(true);
                    setError(null);
                    const data = await getCategories();
                    if (isMounted && data && data.length > 0) {
                        // Kategorileri dönüştürürken Türkçe karakterleri düzelt
                        const processedCategories = data.map(cat => ({
                            ...cat,
                            name: fixTurkishChars(cat.name),
                            nameEn: cat.nameEn
                        }));
                        setCategories(processedCategories);
                    } else if (isMounted) {
                        setError('Kategoriler yüklenemedi.');
                    }
                } catch (err) {
                    if (isMounted) {
                        setError('Kategoriler yüklenirken bir hata oluştu.');
                        console.error('Kategori yükleme hatası:', err);
                    }
                } finally {
                    if (isMounted) {
                        setLoading(false);
                    }
                }
            }
        };

        loadCategories();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Sadece component mount olduğunda çalışsın

    // Telefon numarası formatı ve validation
    const formatPhoneNumber = (value) => {
        // Sadece rakamları kabul et
        return value.replace(/[^\d]/g, '');
    };

    const handlePhoneChange = (e) => {
        const formattedPhone = formatPhoneNumber(e.target.value);
        setPhone(formattedPhone);
    };

    const handleCallRequest = () => {
        // Telefon numarası validasyonu (sadece rakam kontrolü)
        if (!phone || !/^\d+$/.test(phone)) {
            setCallStatus('error');
            return;
        }

        trackEvent('treatment_call_request', {
            treatment_id: treatment?.slug,
            treatment_title: treatment?.title,
            phone: phone
        });

        setCallStatus('success');
        setPhone('');

        setTimeout(() => {
            setCallStatus(null);
        }, 3000);
    };

    const handleAppointmentClick = () => {
        trackEvent('treatment_appointment_click', {
            treatment_id: treatment?.slug,
            treatment_title: treatment?.title,
            source: 'sidebar'
        });
    };

    const handleCategoryClick = (category) => {
        trackEvent('sidebar_category_click', {
            category_id: category.identifier,
            category_name: category.name,
            source: 'treatment_sidebar',
            current_treatment: treatment?.slug
        });
    };

    return (
        <div className="treatment-sidebar">
            {/* Call Request Form */}
            <div className="sidebar-card call-card">
                <div className="call-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 5a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z" fill="currentColor"/>
                    </svg>
                </div>
                <h3>{t('treatmentSidebar.callUs')}</h3>
                <p>{t('treatmentSidebar.callDescription')}</p>
                
                <div className="call-form">
                    <input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder={t('treatmentSidebar.phonePlaceholder')}
                        value={phone}
                        onChange={handlePhoneChange}
                        className={callStatus === 'error' ? 'error' : ''}
                    />
                    <button 
                        className="call-btn"
                        onClick={handleCallRequest}
                        disabled={callStatus === 'success'}
                    >
                        {callStatus === 'success' ? t('treatmentSidebar.sent') : t('treatmentSidebar.callButton')}
                    </button>
                    
                    {callStatus === 'error' && (
                        <div className="status-message error">
                            {t('treatmentSidebar.invalidPhone')}
                        </div>
                    )}
                    {callStatus === 'success' && (
                        <div className="status-message success">
                            {t('treatmentSidebar.successMessage')}
                        </div>
                    )}
                </div>
            </div>

            {/* Appointment CTA */}
            <div className="sidebar-card appointment-card">
                <Link 
                    to={currentLang === 'tr' ? '/contact' : '/en/contact'}
                    className="appointment-btn"
                    onClick={handleAppointmentClick}
                >
                    {t('treatmentSidebar.appointmentButton')}
                </Link>
            </div>

            {/* Kategoriler */}
            <div className="sidebar-card">
                <h3 style={{color: '#232a6c', fontWeight: 600, fontSize: 18, marginBottom: 15}}>
                    {t('blogs.single.categories.title')}
                </h3>
                {loading ? (
                    <p>Yükleniyor...</p>
                ) : error ? (
                    <p style={{color: 'red'}}>{error}</p>
                ) : categories.length > 0 ? (
                    <ul style={{padding: 0, margin: 0, listStyle: 'none'}}>
                        {categories.map((category) => (
                            <li key={category.id} style={{marginBottom: 8}}>
                                <Link 
                                    to={`/${currentLang === 'tr' ? 'tedaviler' : 'treatments'}?category=${category.identifier}`}
                                    style={{color: '#583fbc', textDecoration: 'underline', cursor: 'pointer'}}
                                    onClick={() => handleCategoryClick(category)}
                                >
                                    {currentLang === 'tr' ? fixTurkishChars(category.name) : category.nameEn}
                                </Link>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p>Henüz kategori bulunmuyor.</p>
                )}
            </div>
        </div>
    );
};

export default TreatmentSidebar; 